import {
  accountMutationHeaders, browserAccountStateSessionRepository, readBoundedAccountJson,
} from "../account/account-state-session-repository.js";
import { element, translate } from "../account/identity-view-primitives.js";
import {
  decodeTrackerParticipationResponseV1, decodeTrackerParticipationRegistrationV1,
} from "../account/tracker-participation-contract.js";

const PATH = "/api/v1/account/participation";
const t = (key, fallback) => translate(`participation.${key}`, fallback);
const node = (name, text = "", className = "") => element(name, { text, className });

class FishyTrackerParticipation extends HTMLElement {
  #lifecycle = null;
  #unsubscribe = null;
  #request = null;
  #model = null;
  #status = null;
  #list = null;
  #dialog = null;
  #busy = false;

  connectedCallback() {
    if (this.#lifecycle) return;
    this.#lifecycle = new AbortController();
    const section = node("section", "", "card card-border bg-base-100 shadow-sm");
    const body = node("div", "", "card-body gap-4");
    const heading = node("div", "", "flex flex-wrap items-center justify-between gap-3");
    heading.append(node("h2", t("title", "Participation"), "card-title"));
    const refresh = this.#button(t("refresh", "Refresh status"), () => this.#load());
    heading.append(refresh);
    this.#status = node("p", "", "text-sm leading-6");
    this.#status.setAttribute("role", "status");
    this.#list = node("div", "", "grid gap-4");
    body.append(heading, this.#status, this.#list);
    section.append(body);
    this.#dialog = node("dialog", "", "modal");
    this.#dialog.setAttribute("aria-label", t("review", "Review participation"));
    this.replaceChildren(section, this.#dialog);
    const repository = browserAccountStateSessionRepository();
    this.#unsubscribe = repository.subscribe(state => {
      this.#request?.abort();
      this.#model = null;
      this.#busy = false;
      this.#dialog.close();
      this.#list.replaceChildren();
      if (state.kind === "signedOut") {
        this.#status.textContent = t("signed_out", "Sign in to manage your installations. Local tracking does not require participation.");
      } else if (state.kind === "ready" || state.kind === "unavailable") {
        void this.#load();
      } else {
        this.#status.textContent = t("loading", "Loading participation status…");
      }
    });
    void repository.load();
  }

  disconnectedCallback() {
    this.#lifecycle?.abort();
    this.#lifecycle = null;
    this.#request?.abort();
    this.#unsubscribe?.();
    this.#unsubscribe = null;
    this.#model = null;
    this.#dialog?.close();
  }

  #button(label, action, primary = false) {
    const button = node("button", label, primary ? "btn btn-sm btn-primary" : "btn btn-sm");
    button.type = "button";
    button.addEventListener("click", () => { if (!this.#busy) void action(); }, { signal: this.#lifecycle.signal });
    return button;
  }

  async #load() {
    if (this.#busy) return;
    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;
    this.#status.textContent = t("loading", "Loading participation status…");
    try {
      const response = await fetch(window.__fishystuffResolveApiUrl(PATH), {
        method: "GET", cache: "no-store", credentials: "include", redirect: "error",
        headers: { Accept: "application/json" }, signal: request.signal,
      });
      if (response.status === 401) {
        this.#model = null;
        this.#list.replaceChildren();
        this.#status.textContent = t("signed_out", "Sign in to manage your installations. Local tracking does not require participation.");
        return;
      }
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const model = decodeTrackerParticipationResponseV1(await readBoundedAccountJson(response, "Participation", 256 * 1024));
      if (request.signal.aborted) return;
      this.#model = model;
      this.#render();
    } catch (error) {
      if (!request.signal.aborted) {
        this.#model = null;
        this.#list.replaceChildren();
        this.#status.textContent = `${t("unavailable", "Participation status is unavailable. No change has been confirmed.")} ${error.message}`;
      }
    } finally {
      if (this.#request === request) this.#request = null;
    }
  }

  #render() {
    this.#list.replaceChildren();
    this.#status.textContent = t("intro", "Manage each installation separately. Stopping contribution stops future delivery; it does not erase existing data or replace a privacy-rights request.");
    if (this.#model.registrations.length === 0) {
      const message = this.#model.canAuthorize
        ? t("no_installations", "No installations are registered for this account. In Desktop, choose “Review synchronization and contribution” to review retained history and future uploads. Visiting this page enables nothing.")
        : t("no_installations_unavailable", "No installations are registered for this account. Enabling participation requires current Terms acceptance and Desktop eligibility.");
      this.#list.append(node("p", message, "text-sm leading-6"));
      return;
    }
    for (const registration of this.#model.registrations) {
      const item = node("section", "", "rounded-box border border-base-300 p-4 grid gap-3");
      item.append(node("h3", registration.journalId, "font-semibold break-all"));
      const state = registration.state === "authorized" ? t("active", "Authorized")
        : registration.state === "disabled" ? t("disabled", "Stopped") : t("review_required", "Review required");
      item.append(node("p", state, "text-sm font-medium"));
      if (registration.revocation === "pending") {
        item.append(node("p", t("revocation_pending", "New authorization is disabled. Broker revocation is still pending; do not assume existing connections have stopped."), "text-sm leading-6"));
      } else if (registration.revocation === "completed") {
        item.append(node("p", t("revocation_complete", "Broker revocation confirmed."), "text-sm"));
      }
      const actions = node("div", "", "flex flex-wrap gap-2");
      if (this.#model.canAuthorize && this.#model.disclosure && registration.state === "reviewRequired" && registration.preview && registration.noticeVersion === this.#model.noticeVersion) {
        actions.append(this.#button(t("review", "Review participation"), () => this.#review(registration)));
      } else if (!this.#model.canAuthorize && registration.state !== "authorized") {
        item.append(node("p", t("enable_unavailable", "Enabling requires current Terms acceptance and Desktop eligibility. You can still stop an existing registration here."), "text-sm leading-6"));
      } else if (registration.state !== "authorized") {
        item.append(node("p", t("prepare_again", "Prepare a fresh review in this Desktop installation before enabling contribution."), "text-sm leading-6"));
      }
      if (registration.state !== "disabled" || registration.revocation === "pending") {
        actions.append(this.#button(registration.revocation === "pending"
          ? t("retry_stop", "Retry broker revocation") : t("stop", "Stop contributing"), () => this.#change("disable", registration)));
      }
      item.append(actions);
      this.#list.append(item);
    }
  }

  #review(registration) {
    const locale = registration.locale || (document.documentElement.lang === "de-DE" ? "de-DE" : "en-US");
    const disclosure = this.#model.disclosure[locale];
    const box = node("div", "", "modal-box w-11/12 max-w-3xl grid gap-4");
    box.lang = locale;
    box.append(node("h3", disclosure.title, "text-lg font-bold"));
    box.append(node("p", `${t("account", "Account")}: ${this.#model.accountId}`, "text-sm break-all"));
    box.append(node("p", `${t("installation", "Installation")}: ${registration.journalId}`, "text-sm break-all"));
    for (const paragraph of disclosure.paragraphs) box.append(node("p", paragraph, "text-sm leading-6"));
    box.append(node("h4", t("retained_history", "Retained history prepared by this installation"), "font-semibold"));
    const preview = registration.preview;
    const details = node("dl", "", "grid gap-2 text-sm");
    const row = (label, value) => {
      const wrapper = node("div");
      wrapper.append(node("dt", label, "font-medium"), node("dd", value, "break-words"));
      details.append(wrapper);
    };
    row(t("fact_count", "Pending facts"), String(preview.factCount));
    row(t("fact_kinds", "Fact kinds"), preview.factKinds.join(", ") || t("none", "None"));
    row(t("source_range", "Available source timestamp range (nanoseconds)"),
      `${preview.firstSourceObservedAtNs ?? t("unknown", "Unavailable")} — ${preview.lastSourceObservedAtNs ?? t("unknown", "Unavailable")}`);
    row(t("names", "Identity names in retained facts"), preview.containsIdentityNames ? t("yes", "Yes") : t("no", "No"));
    row(t("gaps", "Recorded history gaps"), preview.hasGaps ? t("yes", "Yes") : t("no", "No"));
    box.append(details);
    const notice = node("a", t("full_notice", "Read the complete Desktop and Dataset Contribution Privacy Notice"), "link text-sm");
    notice.href = `/api/v1/account/desktop-privacy?locale=${locale}`;
    notice.target = "_blank";
    notice.rel = "noopener";
    box.append(notice);
    const actions = node("div", "", "modal-action flex-wrap");
    actions.append(this.#button(t("keep_local", "Keep data local"), () => this.#dialog.close()));
    actions.append(this.#button(t("enable", "Enable synchronization and contribute"), () => this.#change("authorize", registration), true));
    box.append(actions);
    this.#dialog.replaceChildren(box);
    this.#dialog.showModal();
  }

  async #change(action, registration) {
    if (!this.#model || this.#busy) return;
    this.#busy = true;
    const model = this.#model;
    const request = new AbortController();
    this.#request?.abort();
    this.#request = request;
    const buttons = [...this.querySelectorAll("button")];
    for (const button of buttons) button.disabled = true;
    this.#status.textContent = t("saving", "Applying participation change…");
    try {
      const command = {
        schemaVersion: 1, operationId: `op_${crypto.randomUUID().replaceAll("-", "")}`,
        registrationId: registration.registrationId, expectedRevision: registration.revision,
        ...(action === "authorize" ? { noticeVersion: model.noticeVersion, affirmed: true } : {}),
      };
      const response = await fetch(window.__fishystuffResolveApiUrl(`${PATH}/${action}`), {
        method: "POST", cache: "no-store", credentials: "include", redirect: "error",
        headers: accountMutationHeaders({ kind: "ready", ...model }), signal: request.signal,
        body: JSON.stringify(command),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const updated = decodeTrackerParticipationRegistrationV1(await readBoundedAccountJson(response, "Participation change", 32 * 1024));
      if (request.signal.aborted || this.#model !== model) return;
      if (updated.registrationId !== registration.registrationId) throw new Error("Participation changed an unexpected installation.");
      model.registrations = model.registrations.map(item => item.registrationId === updated.registrationId ? updated : item);
      this.#dialog.close();
      this.#render();
    } catch (error) {
      if (!request.signal.aborted) {
        this.#dialog.close();
        this.#model = null;
        this.#list.replaceChildren();
        this.#status.textContent = `${t("uncertain", "Could not confirm this change. Refresh status before trying again; do not assume contribution has stopped.")} ${error.message}`;
      }
    } finally {
      this.#busy = false;
      for (const button of buttons) button.disabled = false;
      if (this.#request === request) this.#request = null;
    }
  }
}

if (!customElements.get("fishy-tracker-participation")) {
  customElements.define("fishy-tracker-participation", FishyTrackerParticipation);
}
